import styled from '@emotion/styled'
import { useRouter } from 'found'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useTypedDispatch, useTypedSelector } from '..'
import { createDefectAction, options as optData } from '../actions/create-defect'
import { fetchEquipmentsAction } from '../actions/defect-common'
import { cacheWarmup } from '../actions/defectAssistant'
import { fetchOpenDefectsAction } from '../actions/defects/index'
import ConfirmationDialog from '../components/create-defect/ConfirmationDialog'
import CreateDefectComponent from '../components/create-defect/CreateDefectComponent'
import { CreateDefectFormData, FieldConfig, ItemData } from '../components/create-defect/types'
import MainMenu from '../components/main-menu/MainMenu'
import { FooterPage } from '../components/page/Page'
import { Container, HeaderText, PageHeader } from '../components/page/PageComponents'
import { DEFAULT_DEFECT_GROUP } from '../constants'
import ArrowLeft from '../icons/ArrowLeft'
import {
  addIdenticalLabels,
  addIdtoDescription,
  convertToDropDownItem,
  createCriticalityDropdownOptions,
  getDefectCodes,
  getEquipmentOptions,
} from '../lib/createDefectFormUtils'
import {
  clearDataByKey,
  clearFiles,
  equipmentsWithHighlightedSelector,
  resetOptionsStatusByKey,
} from '../reducers/createDefectSlice'
import { resetEquipmentsStatus } from '../reducers/defectCommonDataSlice'
import { resetDefects } from '../reducers/defectsSlice'
import { getColor, theme } from '../Theme'

const Constrain = styled.div`
  ${theme.layout.flexRow};
  ${theme.layout.fluidWidth(theme.maxWidths.content)};
  position: relative;
  box-sizing: border-box;
  width: 100%;
`

const PaddedContainer = styled.div`
  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 20px;
`

const BackIcon = styled.div`
  align-content: start;
  display: flex;
  padding-left: 8px;
  color: ${(p) => getColor(p.theme, ['black'], ['white'])};
`

const getDefaultFormValues = (
  defectGroup: string,
  installationGroup: string,
  vehicleType: string
): CreateDefectFormData => ({
  issuedTime: new Date(),
  comment: '',
  aiComment: '',
  digitalAssetUuids: [],
  defectGroup,
  installationGroup,
  installationItem: '',
  productGroup: '',
  defectCode: '',
  defectType: '',
  positionInVehicle: '',
  criticality: '',
  restrictionCode: '',
  affectToUsage: '',
  installationSerialNumber: '',
  vehicleType,
  vehicleSerialNumber: installationGroup,
  type: 'equipment',
})

type Equipment = {
  installationGroup: string
  description: string
  itemId: string
  fleet?: string
}

const TEMP_ROOT_NODE = {
  itemId: '',
  serialNumber: '',
  parentSerialNumber: '',
  description: '',
  parentItemId: '',
  uniqueId: '',
  parentUniqueId: '',
  seperator: '|',
} as const

const getRootNode = (selectedEquipment?: Equipment): ItemData =>
  selectedEquipment
    ? {
        itemId: selectedEquipment.itemId,
        serialNumber: selectedEquipment.installationGroup,
        parentSerialNumber: '',
        description: selectedEquipment.description,
        parentItemId: '',
        uniqueId: `${selectedEquipment.itemId}|${selectedEquipment.installationGroup}`,
        parentUniqueId: '',
        seperator: '|',
      }
    : TEMP_ROOT_NODE

const CreateDefectPage = () => {
  const { t } = useTranslation()
  const dispatch = useTypedDispatch()

  const found = useTypedSelector((state) => state.found)
  const createDefectStatus = useTypedSelector((state) => state.defect.status)
  const defectGroups = useTypedSelector((state) => state.defect.defectGroups)
  const items = useTypedSelector((state) => state.defect.items)
  const defectTrees = useTypedSelector((state) => state.defect.defectTrees)
  const positions = useTypedSelector((state) => state.defect.positions)
  const limitationsOfUsage = useTypedSelector((state) => state.defect.limitationsOfUsage)
  const affectToUsage = useTypedSelector((state) => state.defect.affectToUsage)
  const files = useTypedSelector((state) => state.defect.assetIdentifiers)
  const preSelectedSerialNumbers = useTypedSelector(
    (state) => state.defect.preSelectedSerialNumbers
  )
  const { equipments, highlightedEquipments } = useTypedSelector(equipmentsWithHighlightedSelector)

  const [defaultDefectGroup, setDefaultDefectGroup] = useState<string>('')
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment>()
  const [defectTypes, setDefectTypes] = useState<string[]>([])
  const [attachmentRequired, setAttachmentRequired] = useState<boolean>(false)
  const [openDefectsEnabled] = useState<boolean>(
    found && found?.match?.location?.state?.from !== '/search/defects'
  )

  // check if any options fetch failed
  const options = [
    {
      status: defectGroups.status,
      clearFunc: () => dispatch(resetOptionsStatusByKey('defectGroups')),
    },
    {
      status: items.status,
      clearFunc: () => dispatch(resetOptionsStatusByKey('items')),
    },
    {
      status: defectTrees.status,
      clearFunc: () => dispatch(resetOptionsStatusByKey('defectTrees')),
    },
    {
      status: limitationsOfUsage.status,
      clearFunc: () => dispatch(resetOptionsStatusByKey('limitationsOfUsage')),
    },
    {
      status: affectToUsage.status,
      clearFunc: () => dispatch(resetOptionsStatusByKey('affectToUsage')),
    },
    {
      status: equipments.status,
      clearFunc: () => dispatch(resetEquipmentsStatus()),
    },
    {
      status: positions.status,
      clearFunc: () => dispatch(resetOptionsStatusByKey('positions')),
    },
  ]

  const failedOptions = options.filter((o) => o.status === 'failed').map((o) => o.clearFunc)

  const methods = useForm<CreateDefectFormData>({
    defaultValues: getDefaultFormValues(
      defaultDefectGroup,
      selectedEquipment?.installationGroup ?? '',
      selectedEquipment?.itemId ?? ''
    ),
    mode: 'onTouched',
  })

  const { setValue, getValues, resetField, reset } = methods

  const clearAndfetchLimitationsOfUsage = useCallback(() => {
    const vehicleType = getValues('vehicleType')

    if (vehicleType) {
      resetField('restrictionCode')
      dispatch(optData.fetchLimitationsOfUsageAction({ serializedItemIds: [vehicleType] }))
    } else {
      dispatch(clearDataByKey('limitationsOfUsage'))
      setValue('restrictionCode', '')
    }
  }, [getValues, dispatch, setValue, resetField])

  const resetForm = useCallback(
    (resetEquipment = false): void => {
      reset(
        getDefaultFormValues(
          defaultDefectGroup,
          selectedEquipment?.installationGroup ?? '',
          selectedEquipment?.itemId ?? ''
        )
      )
      if (resetEquipment) setSelectedEquipment(undefined)

      setAttachmentRequired(false)
    },
    [reset, defaultDefectGroup, selectedEquipment]
  )

  // Initialise the form once defect groups load. Intentionally NOT depending on resetForm:
  // resetForm changes identity whenever selectedEquipment changes, and re-running this effect on
  // every päälaite change would reset() the whole form and wipe user input (comment, attachments,
  // category). Equipment changes already reset only the tree-dependent fields via vehicleChanged.
  useEffect(() => {
    setDefaultDefectGroup(
      defectGroups?.data?.some((g) => g.id === DEFAULT_DEFECT_GROUP) ? DEFAULT_DEFECT_GROUP : ''
    )
    resetForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defectGroups?.data, defaultDefectGroup])

  useEffect(() => {
    if (preSelectedSerialNumbers.length === 1 && equipments?.data) {
      const preSelectedEquipment = equipments.data.find(
        (equipment) => equipment.installationGroup === preSelectedSerialNumbers[0]
      )
      if (preSelectedEquipment) {
        setSelectedEquipment(preSelectedEquipment)
        setValue('installationGroup', preSelectedEquipment.installationGroup)
      }
    }
  }, [equipments?.data, setValue, preSelectedSerialNumbers])

  const resetDependentFields = useCallback(
    (field: keyof CreateDefectFormData): void => {
      switch (field) {
        // @ts-expect-error: fallthrough is intentional
        case 'vehicleSerialNumber':
          resetField('installationSerialNumber')
          resetField('installationItem')
        // @ts-expect-error: fallthrough is intentional
        case 'installationSerialNumber':
          resetField('productGroup')
        // @ts-expect-error: fallthrough is intentional
        case 'productGroup':
          resetField('defectType')
        // eslint-disable-next-line no-fallthrough
        case 'defectType':
          resetField('defectCode')
          resetField('criticality')
      }
    },
    [resetField]
  )

  // onChange handlers
  const defectCodeChanged = (value: string | null | undefined): void => {
    resetDependentFields('productGroup')
    if (!value) {
      setDefectTypes([])
      dispatch(clearDataByKey('positions'))
      return
    }
    setDefectTypes(
      defectTrees?.data
        ?.filter((defect) => defect.productGroups === value)
        ?.map((defect) => defect.defectType) ?? []
    )

    if (selectedEquipment)
      dispatch(
        optData.fetchPositionsActions({
          defectCode: value,
          serializedItemId: selectedEquipment.itemId,
        })
      )
  }

  const vehicleChanged = useCallback(
    (value: string | null | undefined, skipReset?: boolean): void => {
      if (!skipReset) resetDependentFields('vehicleSerialNumber')

      const newValue = value ?? ''
      const equipment = equipments?.data?.find(
        (equipment) => equipment.installationGroup === newValue
      )
      const itemId = equipment?.itemId ?? ''
      setSelectedEquipment(equipment)
      // vehicleType is used as installationItem if no sub device is selected
      setValue('type', 'equipment')
      setValue('vehicleType', itemId)
      // use productGroup for serial number when only main device used
      setValue('vehicleSerialNumber', newValue)

      dispatch(clearDataByKey('positions'))

      if (!newValue) {
        dispatch(resetDefects())
        dispatch(clearDataByKey('items'))
        dispatch(clearDataByKey('defectTrees'))
        clearAndfetchLimitationsOfUsage()
        setDefectTypes([])

        return
      }

      if (openDefectsEnabled) {
        dispatch(fetchOpenDefectsAction({ installationGroups: newValue }))
      }

      dispatch(optData.fetchItemsAction({ filter: newValue, itemId }))
      dispatch(optData.fetchDefectTreesAction(itemId))

      // Pre-warm the AI defect-assistant caches (defect tree, template embeddings, physical
      // breakdown) for the selected vehicle so the suggestion call is fast once the worker
      // finishes writing/transcribing. Fire-and-forget; matches the (vehicleType, serialNumber)
      // the later analyze call sends.
      if (itemId) cacheWarmup(itemId, newValue)

      clearAndfetchLimitationsOfUsage()
    },
    [
      dispatch,
      equipments?.data,
      resetDependentFields,
      setValue,
      openDefectsEnabled,
      clearAndfetchLimitationsOfUsage,
    ]
  )

  const defectTypeChanged = (value: string | null | undefined): void => {
    resetDependentFields('defectType')
    const defectCode = getValues('productGroup')
    const defectTree = defectTrees?.data?.find(
      (defect) => defect.productGroups === defectCode && defect.defectType === value
    ) ?? { criticality: '', defectCode: '' }
    setValue('criticality', defectTree.criticality)
    setValue('defectCode', defectTree.defectCode)
  }

  const pendingAiDefectRef = useRef<{ defectCode: string; defectType: string } | null>(null)
  const aiTreeFetchedForItemRef = useRef<string | null>(null)

  const applyAiSuggestion = (defectCode: string, defectType: string): void => {
    // A fresh suggestion pick (no defectType yet) starts a new resolution — clear the AI tree-fetch
    // guard so the finishing step may fetch the resolved item's tree again if needed.
    if (!defectType) aiTreeFetchedForItemRef.current = null

    const trees = defectTrees?.data
    // The AI body's field names mean the opposite of the tree's: body.defectCode is the product
    // group (tree.productGroups, e.g. "JDC") and body.defectType is the full defect code
    // (tree.defectCode, e.g. "JDC63"). The tree's own `defectType` is the human-readable label
    // (e.g. "Toimintahäiriö") that the Vikatyyppi field displays.
    const treeRow = defectType
      ? trees?.find((d) => d.productGroups === defectCode && d.defectCode === defectType) ??
        trees?.find((d) => d.defectCode === defectType)
      : // ProductGroup-only (a suggestion was picked but no specific code yet): reflect the group.
        trees?.find((d) => d.productGroups === defectCode || d.defectCode.startsWith(defectCode))

    if (!treeRow) {
      // The resolved item's defect tree probably isn't loaded yet: the AI flow sets the item via
      // setValue, which doesn't trigger the usual fetch. Fetch it once (guarded against re-fetching
      // the same item) and re-apply the suggestion once it arrives (see the effect below).
      const itemUniqueId = getValues('installationItem')
      const itemId = itemUniqueId ? items.data?.[itemUniqueId]?.itemId : undefined
      if (itemId && defectType && aiTreeFetchedForItemRef.current !== itemId) {
        aiTreeFetchedForItemRef.current = itemId
        pendingAiDefectRef.current = { defectCode, defectType }
        dispatch(optData.fetchDefectTreesAction(itemId))
      }
      return
    }

    setValue('productGroup', treeRow.productGroups)
    defectCodeChanged(treeRow.productGroups)
    if (defectType) {
      setValue('defectType', treeRow.defectType)
      defectTypeChanged(treeRow.defectType)
    }
  }

  // Re-apply a deferred AI suggestion once the resolved item's defect tree has finished loading.
  useEffect(() => {
    if (defectTrees.status !== 'succeeded' || !pendingAiDefectRef.current) return
    const pending = pendingAiDefectRef.current
    pendingAiDefectRef.current = null
    applyAiSuggestion(pending.defectCode, pending.defectType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defectTrees.status, defectTrees.data])

  const defectGroupChanged = (value: string | null | undefined): void => {
    const defectGroup = defectGroups?.data?.find((defectGroup) => defectGroup.id === value) ?? {
      createDefectRequirePicture: false,
    }

    setAttachmentRequired(defectGroup.createDefectRequirePicture)
  }

  const attachmentsChanged = (): void => {
    if (files.data) {
      setValue('digitalAssetUuids', Object.keys(files.data))
    }
  }

  const installationItemChanged = (value: string | null | undefined): void => {
    resetDependentFields('installationSerialNumber')
    const item = items.data?.[value ?? '']
    const serialNumber = item?.serialNumber ?? ''
    setValue('installationSerialNumber', serialNumber ?? '')

    dispatch(clearDataByKey('defectTrees'))

    if (item) {
      setValue('type', 'item')
      dispatch(optData.fetchDefectTreesAction(item.itemId))
      return
    }

    vehicleChanged(selectedEquipment?.installationGroup, true)
  }
  // onChange handlers end

  const onSubmit = (payload: CreateDefectFormData): void => {
    dispatch(
      createDefectAction({
        ...payload,
        installationItem: items.data?.[payload.installationItem]?.itemId ?? '',
      })
    )
  }

  const scrollToTop = () => {
    document.getElementById('create-defect-scroll-anchor')?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    dispatch(fetchEquipmentsAction(null))
    dispatch(optData.fetchDefectGroupsAction(null))
    dispatch(optData.fetchAffectToUsageAction(null))
  }, [dispatch])

  useEffect(() => {
    if (preSelectedSerialNumbers.length === 1 && equipments?.data?.length) {
      vehicleChanged(preSelectedSerialNumbers[0], false)
    }
  }, [dispatch, equipments, preSelectedSerialNumbers, vehicleChanged])

  const fields: FieldConfig = {
    defectGroup: {
      options: defectGroups?.data?.map((o) => convertToDropDownItem(o, 'id', 'description')) ?? [],
      onChange: defectGroupChanged,
      required: true,
      status: defectGroups.status,
    },
    installationGroup: {
      options: getEquipmentOptions(equipments.data, highlightedEquipments),
      onChange: vehicleChanged,
      required: true,
      status: equipments.status,
    },
    installationItem: {
      options: items.data ?? {},
      root: getRootNode(selectedEquipment),
      onChange: installationItemChanged,
      status: items.status,
    },
    productGroup: {
      options: getDefectCodes(defectTrees.data ?? []),
      onChange: defectCodeChanged,
      required: true,
      status: defectTrees.status,
      multiline: true,
    },
    defectType: {
      options: addIdenticalLabels([...new Set(defectTypes)]),
      onChange: defectTypeChanged,
      required: true,
      status: defectTrees.status,
    },
    positionInVehicle: {
      options: positions.data
        ? positions.data.map((pos) => ({ value: pos.position, label: pos.description }))
        : [],
      status: positions.status,
    },
    criticality: {
      options: createCriticalityDropdownOptions(t),
      status: 'succeeded',
    },
    restrictionCode: {
      options: addIdtoDescription(
        limitationsOfUsage.data?.filter(({ id }) => id.startsWith('P')) || []
      ),
      status: limitationsOfUsage.status,
    },
    affectToUsage: {
      options: addIdtoDescription(affectToUsage.data || []),
      status: affectToUsage.status,
    },
    digitalAssetUuids: {
      required: attachmentRequired,
      onChange: attachmentsChanged,
      files: files.data ?? {},
      status: files.status,
    },
    issuedTime: {},
    comment: {},
  }

  const flowStep = useTypedSelector((state) => state.createDefectAssistant.flow.step)
  const assetBytes = useTypedSelector((state) => state.defect.assetBytes)
  const { formState } = methods
  const hasAiStarted = flowStep !== 'idle'
  const hasImages = Object.keys(assetBytes).length > 0
  const isDirty = hasAiStarted || hasImages || formState.isDirty

  const { router } = useRouter()
  const isIntentionalLeave = useRef(false)

  const [showLeaveDialog, setShowLeaveDialog] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null)

  const tryNavigate = useCallback(
    (navigate: () => void) => {
      if (isDirty) {
        setPendingNavigation(() => navigate)
        setShowLeaveDialog(true)
      } else {
        navigate()
      }
    },
    [isDirty]
  )

  // Leaves the page: runs the queued navigation. Bound to the dialog's secondary ("Keskeytä") button.
  const confirmLeave = () => {
    isIntentionalLeave.current = true
    dispatch(clearFiles())
    setShowLeaveDialog(false)
    pendingNavigation?.()
    setPendingNavigation(null)
  }

  // Stays on the page: dismisses the dialog. Bound to the dialog's primary ("Jatka täyttämistä") button.
  const cancelLeave = () => {
    setShowLeaveDialog(false)
    setPendingNavigation(null)
  }

  // Guard browser-level navigation (tab close, page reload) — suppressed when we confirmed leaving
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      if (isIntentionalLeave.current) return
      e.preventDefault()
      // Legacy browsers (Safari, older Chrome) require returnValue to be set to show the prompt.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  useEffect(() => {
    scrollToTop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetForm])

  return (
    <FooterPage
      footer={
        // found 1.3.0 exposes no route-leave hook, so we intercept MainMenu's <Link> clicks in the
        // capture phase (before found navigates) and route them through the dirty-check. Only
        // in-app paths are handled; external/hash links fall through to default behaviour.
        <div
          onClickCapture={(e) => {
            if (!isDirty) return
            if (!(e.target instanceof Element)) return
            const anchor = e.target.closest('a')
            const path = anchor?.getAttribute('href')
            if (!path || !path.startsWith('/')) return
            e.preventDefault()
            e.stopPropagation()
            tryNavigate(() => router.push(path))
          }}
        >
          <MainMenu />
        </div>
      }
      footerHeight={theme.maxHeights.footer}
      overflowVisible
    >
      {showLeaveDialog && (
        // ConfirmationDialog wires primary→submitHandler and secondary→closeDialog. We keep the safe
        // option ("Jatka täyttämistä" / stay) as the prominent primary button, so primary=cancelLeave
        // and secondary=confirmLeave (which performs the navigation).
        <ConfirmationDialog
          dialogText={t('defect.create.abandonConfirm')}
          primaryActionText={t('defect.create.abandonStay')}
          secondaryActionText={t('defect.create.abandonLeave')}
          closeDialog={confirmLeave}
          submitHandler={cancelLeave}
        />
      )}
      <div id="create-defect-scroll-anchor" />
      <Container>
        <PageHeader onClick={() => tryNavigate(() => window.history.back())} height="64px">
          <Constrain>
            <BackIcon>
              <ArrowLeft iconSize="normal" />
            </BackIcon>
            <HeaderText>{t('defect.create.title')}</HeaderText>
          </Constrain>
        </PageHeader>
        <PaddedContainer>
          <CreateDefectComponent
            fields={fields}
            onSubmit={onSubmit}
            createDefectStatus={createDefectStatus}
            formMethods={methods}
            failedToFetchOptions={failedOptions}
            resetForm={resetForm}
            selectedEquipment={selectedEquipment}
            scrollToTop={scrollToTop}
            openDefectsEnabled={openDefectsEnabled}
            applyAiSuggestion={applyAiSuggestion}
          />
        </PaddedContainer>
      </Container>
    </FooterPage>
  )
}

export default CreateDefectPage
